import { useEffect, useRef } from 'react';
import { EMPTY_ADDRESS } from './AddressFields';

/**
 * Pre-selected answers, per the source document.
 *
 * These are *materialised into form_data* the first time the client lands on
 * a step (see `useSectionDefaults`) rather than only being shown as selected
 * in the UI. If we only defaulted at render time, a client who accepted every
 * default would submit a form whose stored data disagreed with what they saw,
 * and the exported PDF would show blanks for questions they did answer.
 */
export const SECTION_DEFAULTS = {
  contact: {
    known_by_other_name: 'no',
    citizenship: 'usa',
    residence: 'usa',
    birth_country: 'usa',
    address: { ...EMPTY_ADDRESS },
  },
  employment: {
    employer_address: { ...EMPTY_ADDRESS },
  },
  compliance: {
    exchange_affiliated: 'no',
    public_company_insider: 'no',
    paperless_delivery: 'yes',
    quarterly_reports_portal: 'yes',
  },
  trusted_contact: {
    address: { ...EMPTY_ADDRESS },
  },
  beneficiaries: {},
};

/**
 * Seed a section's defaults once, on first mount, without clobbering
 * anything the client has already answered.
 */
export function useSectionDefaults(data, onChange, defaults) {
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    if (!defaults) return;

    const missing = Object.keys(defaults)
      .filter((key) => data?.[key] === undefined);
    if (missing.length === 0) return;

    const seed = {};
    missing.forEach((key) => { seed[key] = defaults[key]; });
    onChange({ ...seed, ...(data || {}) });
    // Mount-only on purpose: this is a one-time seed, not a sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
